import {
  BarChart3,
  BriefcaseBusiness,
  Building2,
  Landmark,
  ListTodo,
  Megaphone,
  Network,
  PhoneCall,
  ReceiptText,
  Repeat2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  WalletCards,
} from 'lucide-react';
import type { NavItem } from './types';

export const SESSION_KEY = 'zetaOpsSession';
export const ACCESS_TOKEN_KEY = 'accessToken';

export const metricStripSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(4, minmax(150px, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
  },
  gap: { xs: 1.25, md: 1.5 },
  overflowX: 'auto',
  pb: 0.5,
} as const;

export const navItems: NavItem[] = [
  {
    key: 'platform',
    label: 'Platform',
    icon: Building2,
    roles: ['ROOT'],
    platform: 'shared',
  },
  {
    key: 'overview',
    label: 'Overview',
    icon: BarChart3,
    roles: [
      'ADMIN',
      'OPS_AGENT',
      'OPS_MANAGER',
      'OPERATIONS_MANAGER',
      'CLIENT_SERVICING',
      'CUSTOMER_SERVICE',
      'CUSTOMER_CARE',
      'CUSTOMER_CARE_AGENT',
      'COMPLAINTS_HANDLER',
      'COMPLIANCE_USER',
      'COMPLIANCE_SUPERVISOR',
      'SUPERVISOR',
      'PAYMENTS',
      'DOCUMENTS',
      'TRADING',
      'TRANSFERS',
      'SUPPORT_ENGINEER',
    ],
    platform: 'wealth',
  },
  {
    key: 'clients',
    label: 'Customer Support',
    icon: PhoneCall,
    roles: [
      'ADMIN',
      'CLIENT_SERVICING',
      'CUSTOMER_SERVICE',
      'CUSTOMER_CARE',
      'CUSTOMER_CARE_AGENT',
    ],
    platform: 'wealth',
  },
  {
    key: 'accountManagement',
    label: 'Account Management',
    icon: Landmark,
    roles: ['ADMIN', 'OPS_AGENT', 'OPS_MANAGER'],
    platform: 'wealth',
  },
  {
    key: 'users',
    label: 'User Management',
    icon: Users,
    roles: ['ADMIN'],
    platform: 'shared',
  },
  {
    key: 'configuration',
    label: 'Product Configuration',
    icon: SlidersHorizontal,
    roles: ['ADMIN'],
    platform: 'shared',
  },
  {
    key: 'fees',
    label: 'Fee & Charges',
    icon: ReceiptText,
    roles: ['ADMIN'],
    platform: 'wealth',
  },
  {
    key: 'broadcastNotices',
    label: 'Broadcast Notices',
    icon: Megaphone,
    roles: ['ADMIN'],
    platform: 'shared',
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    icon: Repeat2,
    roles: ['ADMIN'],
    platform: 'wealth',
  },
  {
    key: 'cases',
    label: 'Case Management',
    icon: BriefcaseBusiness,
    roles: [
      'ADMIN',
      'OPS_MANAGER',
      'OPERATIONS_MANAGER',
      'CLIENT_SERVICING',
      'CUSTOMER_CARE',
      'CUSTOMER_CARE_AGENT',
      'OPS_AGENT',
      'COMPLAINTS_HANDLER',
      'COMPLIANCE_USER',
      'PAYMENTS',
      'DOCUMENTS',
      'TRADING',
      'TRANSFERS',
      'SUPPORT_ENGINEER',
    ],
    platform: 'wealth',
  },
  {
    key: 'kycAml',
    label: 'Customer Duediligence',
    icon: ShieldCheck,
    roles: [
      'ADMIN',
      'MLRO',
      'COMPLIANCE_USER',
      'COMPLIANCE_OFFICER',
      'COMPLIANCE_SUPERVISOR',
      'SUPERVISOR',
      'KYC_ANALYST',
      'KYC_MANAGER',
    ],
    platform: 'wealth',
  },
  {
    key: 'aml',
    label: 'Transaction Monitoring',
    icon: Network,
    roles: [
      'ADMIN',
      'MLRO',
      'COMPLIANCE_USER',
      'COMPLIANCE_OFFICER',
      'COMPLIANCE_SUPERVISOR',
      'SUPERVISOR',
      'AML_ANALYST',
      'AML_MANAGER',
      'KYC_ANALYST',
      'KYC_MANAGER',
    ],
    platform: 'wealth',
  },
  {
    key: 'tasks',
    label: 'Tasks & Reports',
    icon: ListTodo,
    roles: [
      'ADMIN',
      'OPS_MANAGER',
      'OPERATIONS_MANAGER',
      'COMPLIANCE_USER',
      'COMPLAINTS_HANDLER',
      'PAYMENTS',
      'DOCUMENTS',
      'TRADING',
      'TRANSFERS',
      'SUPPORT_ENGINEER',
    ],
    platform: 'wealth',
  },
  {
    key: 'capitalRaise',
    label: 'Private Equity',
    icon: WalletCards,
    roles: [
      'ADMIN',
      'OPS_MANAGER',
      'OPERATIONS_MANAGER',
      'FUND_MANAGER',
      'FUND_OPERATIONS',
      'FUND_VIEWER',
    ],
    platform: 'privateEquity',
  },
];

export const statusOptions = [
  '',
  'NEW',
  'WORKFLOW_STARTED',
  'TRIAGE_IN_PROGRESS',
  'TRIAGED',
  'ASSIGNED_TO_AI',
  'AI_PROCESSING',
  'PENDING_GUARDRAIL_CHECK',
  'ASSIGNED_TO_HUMAN',
  'PENDING_HUMAN_REVIEW',
  'PENDING_CUSTOMER_INFO',
  'PENDING_EXTERNAL_PROVIDER',
  'PENDING_APPROVAL',
  'RESPONSE_READY',
  'RESPONDED',
  'RESOLVED',
  'CLOSED',
  'REOPENED',
  'FAILED',
  'CANCELLED',
  'OPEN',
  'IN_PROGRESS',
  'WAITING_ON_CLIENT',
  'WAITING_ON_ADVISER',
  'WAITING_ON_SYSTEM',
  'READY_FOR_REVIEW',
  'COMPLETED',
];
export const caseStatusFilterOptions = [
  '',
  'OPEN_CASES',
  'CLOSED_CASES',
  ...statusOptions.filter(Boolean),
];
export const roleOptions = [
  '',
  'OPERATIONS',
  'OPS_AGENT',
  'OPS_MANAGER',
  'CLIENT_SERVICING',
  'FUND_MANAGER',
  'FUND_OPERATIONS',
  'FUND_VIEWER',
  'COMPLAINTS_HANDLER',
  'MLRO',
  'COMPLIANCE_USER',
  'COMPLIANCE_OFFICER',
  'COMPLIANCE_SUPERVISOR',
  'SUPERVISOR',
  'SUPPORT_ENGINEER',
  'PAYMENTS',
  'DOCUMENTS',
];
export const operationsUserRoleOptions = [
  'ADMIN',
  'OPS_AGENT',
  'OPS_MANAGER',
  'CLIENT_SERVICING',
  'FUND_MANAGER',
  'FUND_OPERATIONS',
  'FUND_VIEWER',
  'CUSTOMER_CARE',
  'CUSTOMER_CARE_AGENT',
  'COMPLAINTS_HANDLER',
  'MLRO',
  'COMPLIANCE_USER',
  'COMPLIANCE_OFFICER',
  'SUPPORT_ENGINEER',
];
export const caseQueueOptions = [
  'GENERAL_SERVICING',
  'CLIENT_SERVICING',
  'PAYMENTS',
  'DOCUMENTS',
  'COMPLAINTS_HANDLER',
  'MLRO',
  'COMPLIANCE_USER',
  'COMPLIANCE_OFFICER',
  'COMPLIANCE_SUPERVISOR',
  'SUPPORT_ENGINEER',
  'OPS_AGENT',
  'OPS_MANAGER',
];
export const priorityOptions = [
  'LOW',
  'NORMAL',
  'HIGH',
  'URGENT',
  'REGULATORY_DEADLINE',
];
export const clientCareCaseTypes = [
  {
    value: 'CLIENT_SERVICING',
    label: 'General client servicing',
    assignedRole: 'CLIENT_SERVICING',
  },
  {
    value: 'CALL_FOLLOW_UP',
    label: 'Call follow-up',
    assignedRole: 'CLIENT_SERVICING',
  },
  {
    value: 'PAYMENT_QUERY',
    label: 'Cash deposit / withdrawal',
    assignedRole: 'PAYMENTS',
  },
  {
    value: 'TRADE_QUERY',
    label: 'Trade or order query',
    assignedRole: 'TRADING',
  },
  {
    value: 'TRANSFER_QUERY',
    label: 'Transfer query',
    assignedRole: 'TRANSFERS',
  },
  {
    value: 'ACCOUNT_CLOSURE_FULL',
    label: 'Account closure journey',
    assignedRole: 'CLIENT_SERVICING',
  },
  {
    value: 'DOCUMENT_REQUEST',
    label: 'Document request',
    assignedRole: 'DOCUMENTS',
  },
  {
    value: 'COMPLAINT_REVIEW',
    label: 'Complaint or escalation',
    assignedRole: 'COMPLAINTS_HANDLER',
  },
  {
    value: 'KYC_AML_SCREENING_REVIEW',
    label: 'KYC / AML review',
    assignedRole: 'COMPLIANCE_USER',
  },
  {
    value: 'CONSOLIDATED_TAX_CERTIFICATE',
    label: 'Consolidated tax certificate',
    assignedRole: 'DOCUMENTS',
  },
  {
    value: 'DATA_RETENTION_DELETION',
    label: 'Data retention / deletion',
    assignedRole: 'COMPLIANCE_USER',
  },
  {
    value: 'JISA_MATURITY',
    label: 'JISA maturity',
    assignedRole: 'CLIENT_SERVICING',
  },
  {
    value: 'JISA_WITHDRAWAL_EXCEPTION',
    label: 'JISA withdrawal exception',
    assignedRole: 'CLIENT_SERVICING',
  },
  {
    value: 'DECEASED_CUSTOMER',
    label: 'Deceased customer',
    assignedRole: 'CLIENT_SERVICING',
  },
];
export const workflowSteps = [
  {
    status: 'WORKFLOW_STARTED',
    label: 'Intake',
    description: 'Case captured and workflow started',
  },
  {
    status: 'TRIAGE_IN_PROGRESS',
    label: 'Triage',
    description: 'Intent, risk, SLA, and route are reviewed',
  },
  {
    status: 'ASSIGNED_TO_HUMAN',
    label: 'Ownership',
    description: 'AI, automation, or operations queue owns the case',
  },
  {
    status: 'PENDING_APPROVAL',
    label: 'Decision',
    description: 'Evidence, response, or manager approval is prepared',
  },
  {
    status: 'RESOLVED',
    label: 'Resolution',
    description: 'Outcome is ready for client record and archive',
  },
];
